import React, { useState, useEffect, useRef } from 'react';
import {
  Activity,
  Zap,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Database,
  Wifi,
  WifiOff,
  ShieldCheck
} from 'lucide-react';
import MedicionesChart from './components/MedicionesChart';
import AlarmasPanel from './components/AlarmasPanel';
import OrdenesPanel from './components/OrdenesPanel';
import NovedadesPanel from './components/NovedadesPanel';
import ConfigPanel from './components/ConfigPanel';
import InformeDiario from './components/InformeDiario';
import InformeCortes from './components/InformeCortes';
import AuditoriaCammesa from './components/AuditoriaCammesa';
import Documentacion from './components/Documentacion';
import SistemaAlarmas, { CammesaAlertPopup } from './components/SistemaAlarmas';
import TensionesCorrientesChart from './components/TensionesCorrientesChart';
import { apiService } from './services/apiService';
import { mqttService } from './services/mqttService';

function App() {
  const [systemHealth, setSystemHealth] = useState(null);
  const [mediciones, setMediciones] = useState([]);
  const [currentData, setCurrentData] = useState(null);
  const [plantas, setPlantas] = useState([]);
  const [selectedPlanta, setSelectedPlanta] = useState('planta');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [mqttConnected, setMqttConnected] = useState(false);
  const [plantasEstado, setPlantasEstado] = useState([]);
  const [showDisconnectedAlert, setShowDisconnectedAlert] = useState(false);
  const [timeRange, setTimeRange] = useState('24h');
  const [lastDataTime, setLastDataTime] = useState(null);
  const [commStatus, setCommStatus] = useState('unknown'); // 'online', 'warning', 'offline', 'unknown'
  const [cammesaAlert, setCammesaAlert] = useState(null); // Mensaje crítico de CAMMESA
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ user: '', pass: '' });
  const [loginError, setLoginError] = useState(false);
  const [alarmas, setAlarmas] = useState([]);
  const alarmaFlagsRef = useRef({});
  const MQTT_HABILITADO = Boolean(
    process.env.REACT_APP_MQTT_WS && !process.env.REACT_APP_MQTT_WS.includes('localhost')
  );

  // Verificación inicial de sesión
  useEffect(() => {
    const session = localStorage.getItem('cammesa_session');
    if (session === 'active') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (loginData.user === 'Gvbio' && loginData.pass === 'Gvbio2026#') {
      setIsAuthenticated(true);
      localStorage.setItem('cammesa_session', 'active');
      localStorage.removeItem('alarmas_locales');
      setAlarmas([]);
      setLoginError(false);
    } else {
      setLoginError(true);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('cammesa_session');
  };

  // Cargar datos iniciales
  useEffect(() => {
    if (!isAuthenticated) return;
    loadInitialData();
    const interval = setInterval(loadSystemHealth, 5000);
    const estadoInterval = setInterval(checkPlantasEstado, 30000);
    const commInterval = setInterval(checkCommunicationStatus, 5000);
    checkPlantasEstado();
    return () => {
      clearInterval(interval);
      clearInterval(estadoInterval);
      clearInterval(commInterval);
    };
  }, [isAuthenticated]);

  // Conectar a MQTT
  useEffect(() => {
    mqttService.connect((topic, message) => {
      if (topic.includes('/mediciones')) {
        setCurrentData(message);
      }
    });

    mqttService.onConnect(() => setMqttConnected(true));
    mqttService.onDisconnect(() => setMqttConnected(false));

    return () => mqttService.disconnect();
  }, []);

  // Monitor de Alarmas en tiempo real
  useEffect(() => {
    if (!isAuthenticated || !currentData) return;

    const nuevasAlarmas = [];
    const ahora = new Date().getTime();
    const potencia = currentData.kwDesp || 0;
    const tension = currentData.tension_vab || currentData.tension_l1 || currentData.tension_promedio || 0;
    const datalinkStatus = systemHealth?.datalink?.status;
    const commPerdida = datalinkStatus === 'SIN_COMUNICACION' || datalinkStatus === 'SIN_DATOS';
    const commOk = datalinkStatus === 'ONLINE_GENERANDO' || datalinkStatus === 'ONLINE_SIN_GENERACION';
    const tensionValida = tension > 0;

    // 1. Detección de Parada de Generador (Tensión presente, Potencia 0)
    if (tensionValida && tension > 5000 && potencia < 50) {
      if (!alarmaFlagsRef.current.paradaGen) {
        nuevasAlarmas.push({
          id: `gen-stop-${ahora}`,
          tipo: 'CRÍTICO',
          mensaje: 'GENERADOR DETENIDO (Tensión presente, Potencia < 50kW)',
          timestamp: new Date().toISOString()
        });
        alarmaFlagsRef.current.paradaGen = true;
      }
    } else if (alarmaFlagsRef.current.paradaGen) {
      alarmaFlagsRef.current.paradaGen = false;
    }

    // 2. Corte de energía solo si hay lectura de tensión válida (evita falsos positivos por MQTT)
    if (tensionValida && tension < 5000) {
      if (!alarmaFlagsRef.current.corteEnergia) {
        nuevasAlarmas.push({
          id: `power-cut-${ahora}`,
          tipo: 'ALERTA',
          mensaje: 'CORTE DE ENERGÍA EXTERNA (Tensión < 5kV)',
          timestamp: new Date().toISOString()
        });
        alarmaFlagsRef.current.corteEnergia = true;
      }
    } else if (alarmaFlagsRef.current.corteEnergia) {
      alarmaFlagsRef.current.corteEnergia = false;
    }

    // 3. Corte de comunicación: usar estado del servidor (datalink), no cálculo local
    if (commPerdida) {
      if (!alarmaFlagsRef.current.corteDatos) {
        nuevasAlarmas.push({
          id: `data-cut-${ahora}`,
          tipo: 'SISTEMA',
          mensaje: 'CORTE DE COMUNICACIÓN (Sin datos entrantes)',
          timestamp: new Date().toISOString()
        });
        alarmaFlagsRef.current.corteDatos = true;
      }
    } else if (commOk && alarmaFlagsRef.current.corteDatos) {
      alarmaFlagsRef.current.corteDatos = false;
    }

    // 4. Detección de Caída de Base de Datos
    if (systemHealth && !systemHealth.connections?.database) {
      if (!alarmaFlagsRef.current.dbDown) {
        nuevasAlarmas.push({
          id: `db-down-${ahora}`,
          tipo: 'ALERTA',
          mensaje: 'ERROR DE CONEXIÓN CON BASE DE DATOS',
          timestamp: new Date().toISOString()
        });
        alarmaFlagsRef.current.dbDown = true;
      }
    } else if (alarmaFlagsRef.current.dbDown) {
      alarmaFlagsRef.current.dbDown = false;
    }

    // 5. MQTT solo si está habilitado en el dashboard (en producción usa REST)
    if (MQTT_HABILITADO && !mqttConnected) {
      if (!alarmaFlagsRef.current.mqttDown) {
        nuevasAlarmas.push({
          id: `mqtt-down-${ahora}`,
          tipo: 'SISTEMA',
          mensaje: 'SERVER OFFLINE / FALLO DE INTERNET (MQTT Desconectado)',
          timestamp: new Date().toISOString()
        });
        alarmaFlagsRef.current.mqttDown = true;
      }
    } else if (alarmaFlagsRef.current.mqttDown) {
      alarmaFlagsRef.current.mqttDown = false;
    }

    if (nuevasAlarmas.length > 0) {
      setAlarmas(prev => {
        const actualizadas = [...nuevasAlarmas, ...prev].slice(0, 10);
        localStorage.setItem('alarmas_locales', JSON.stringify(actualizadas));
        return actualizadas;
      });
    }

  }, [currentData, isAuthenticated, mqttConnected, systemHealth, MQTT_HABILITADO]);

  // Cargar alarmas persistidas al inicio
  useEffect(() => {
    const guardadas = localStorage.getItem('alarmas_locales');
    if (guardadas) {
      try {
        setAlarmas(JSON.parse(guardadas));
      } catch (e) {
        console.error('Error cargando alarmas persistidas');
      }
    }
  }, []);
  // Tarjetas en tiempo real: polling cada 1s (planta envía cada ~10s)
  useEffect(() => {
    if (!selectedPlanta) return;

    const refreshCurrent = () => loadCurrentData();
    refreshCurrent();
    const interval = setInterval(refreshCurrent, 1000);
    return () => clearInterval(interval);
  }, [selectedPlanta]);

  // Histórico: carga al cambiar planta/rango y refresco cada 30s
  useEffect(() => {
    if (!selectedPlanta) return;
    loadHistorico(selectedPlanta, timeRange);
    const interval = setInterval(() => loadHistorico(selectedPlanta, timeRange), 30000);
    return () => clearInterval(interval);
  }, [selectedPlanta, timeRange]);

  const loadInitialData = async () => {
    try {
      const [healthData, plantasData] = await Promise.all([
        apiService.getHealth(),
        apiService.getPlantas()
      ]);

      setSystemHealth(healthData);
      setPlantas(plantasData);

      if (plantasData.length > 0) {
        setSelectedPlanta(plantasData[0].id);
      }
    } catch (error) {
      console.error('Error cargando datos iniciales:', error);
    }
  };

  const loadSystemHealth = async () => {
    try {
      const data = await apiService.getHealth();
      setSystemHealth(data);

      const datalink = data.datalink;
      if (datalink) {
        const secs = datalink.secondsSinceLastData ?? 999;
        if (datalink.status === 'SIN_COMUNICACION' || datalink.status === 'SIN_DATOS') {
          setCommStatus('offline');
        } else if (secs <= 15) {
          setCommStatus('online');
        } else if (secs <= 45) {
          setCommStatus('warning');
        } else {
          setCommStatus('offline');
        }
        if (datalink.lastDataTimestamp) {
          setLastDataTime(new Date(datalink.lastDataTimestamp));
        }
      }
    } catch (error) {
      console.error('Error cargando health:', error);
    }
  };

  const applyCurrentData = (record) => {
    if (!record) return;

    const kw = Number(record.kwDesp || 0);
    const isKwOutlier = isNaN(kw) || kw > 10000 || kw < -100;
    const processedData = { ...record };

    if (isKwOutlier) {
      processedData.kwDesp = 0;
      processedData.kVARDesp = 0;
      processedData.kVAR = 0;
      processedData.kVA = 0;
    }

    const parsedDate = new Date(record.timestamp);
    if (!isNaN(parsedDate.getTime())) {
      const diffSeconds = (new Date() - parsedDate) / 1000;
      processedData.isStale = diffSeconds > 20;
      setCurrentData(processedData);
      setLastDataTime(parsedDate);

      if (diffSeconds <= 20) {
        setCommStatus('online');
      } else if (diffSeconds <= 45) {
        setCommStatus('warning');
      } else {
        setCommStatus('offline');
      }
    }
  };

  const loadCurrentData = async () => {
    try {
      const latest = await apiService.getUltimaMedicion();
      if (latest) {
        applyCurrentData(latest);
      }
    } catch (error) {
      console.error('Error cargando dato actual:', error);
    }
  };

  const loadHistorico = async (plantaId, range = '24h') => {
    try {
      // Calcular horas según rango de tiempo
      const hoursMap = {
        '1h': 1,
        '6h': 6,
        '24h': 24,
        '7d': 168,   // 7 * 24
        '30d': 720   // 30 * 24
      };
      const horas = hoursMap[range] || 24;

      // Calcular límite de registros
      // Datos llegan cada ~3 segundos: 1200 registros/hora
      const limitMap = {
        '1h': 1500,      // ~1 hora
        '6h': 8000,      // ~6 horas
        '24h': 30000,    // ~24 horas (aprox 18k a 20k reales)
        '7d': 50000,     // top para no saturar 
        '30d': 50000     // top para no saturar 
      };
      const limit = limitMap[range] || 30000;

      // Ahora pasamos el parámetro 'horas' para que el servidor filtre por tiempo
      const data = await apiService.getMediciones(plantaId, limit, horas);

      // Ya no necesitamos filtrar en el frontend, el servidor ya lo hace
      const reversedData = data.reverse();

      // Último registro real de la BD (sin punto sintético)
      const lastRealRecord = reversedData.length > 0 ? reversedData[reversedData.length - 1] : null;

      // Punto cero sintético solo para el gráfico histórico (no afecta tarjetas en tiempo real)
      const chartData = [...reversedData];
      if (lastRealRecord) {
        const lastTimestamp = new Date(lastRealRecord.timestamp);
        const now = new Date();
        const diffMinutes = (now - lastTimestamp) / (1000 * 60);

        if (diffMinutes > 2) {
          chartData.push({
            ...lastRealRecord,
            timestamp: now.toISOString(),
            kwDesp: 0,
            kVARDesp: 0,
            kVAR: 0,
            kVA: 0,
            kwGen: 0,
            frecuencia: 0,
            factor_potencia: 0,
            cosfi: 0,
            isSyntheticZero: true
          });
        }
      }

      setMediciones(chartData);
    } catch (error) {
      console.error('Error cargando histórico:', error);
    }
  };

  // Check communication status based on last data time
  const checkCommunicationStatus = () => {
    if (!lastDataTime) {
      setCommStatus('unknown');
      return;
    }
    const now = new Date();
    const diffSeconds = (now - lastDataTime) / 1000;

    if (diffSeconds <= 20) {
      setCommStatus('online');
    } else if (diffSeconds <= 45) {
      setCommStatus('warning');
    } else {
      setCommStatus('offline');
    }
  };

  const checkPlantasEstado = async () => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://cammesa-servidor-770702622935.us-central1.run.app';
      const response = await fetch(`${apiBaseUrl}/api/plantas/estado`);
      const data = await response.json();

      if (data.ok) {
        setPlantasEstado(data.plantas);

        // Mostrar alerta si hay plantas desconectadas
        const desconectadas = data.plantas.filter(p => p.estado === 'desconectado');
        if (desconectadas.length > 0) {
          setShowDisconnectedAlert(true);
        } else {
          setShowDisconnectedAlert(false);
        }
      }
    } catch (error) {
      console.error('Error verificando estado de plantas:', error);
    }
  };

  const formatUptime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 px-4">
        <div className="max-w-md w-full space-y-8 p-10 bg-white rounded-xl shadow-2xl border border-gray-200">
          <div className="text-center">
            <Activity className="mx-auto h-12 w-12 text-blue-600 animate-pulse" />
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900 text-center w-full">
              CAMMESA SOTR
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Ingrese sus credenciales para acceder al Dashboard
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleLogin}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <input
                  type="text"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="Usuario"
                  value={loginData.user}
                  onChange={(e) => setLoginData({ ...loginData, user: e.target.value })}
                />
              </div>
              <div>
                <input
                  type="password"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="Contraseña"
                  value={loginData.pass}
                  onChange={(e) => setLoginData({ ...loginData, pass: e.target.value })}
                />
              </div>
            </div>

            {loginError && (
              <div className="text-red-500 text-sm text-center font-medium">
                ❌ Credenciales incorrectas
              </div>
            )}

            <div>
              <button
                type="submit"
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Ingresar al Sistema
              </button>
            </div>
          </form>
          <div className="text-center text-xs text-gray-400">
            AutoLink Solutions Industrial SOTR v1.0
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Activity className="w-8 h-8 text-primary-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">CAMMESA Gateway</h1>
                <p className="text-sm text-gray-500">Sistema de Telemetría OPC UA / MQTT</p>
              </div>
            </div>

            {/* Status Indicators */}
            <div className="flex items-center space-x-6">
              {systemHealth && (
                <>
                  <StatusBadge
                    icon={Database}
                    label="OPC UA"
                    connected={systemHealth.connections?.opcua}
                  />
                  <StatusBadge
                    icon={systemHealth.connections?.mqtt ? Activity : XCircle}
                    label="Broker Nube"
                    connected={systemHealth.connections?.mqtt}
                  />
                  <StatusBadge
                    icon={Database}
                    label="Database"
                    connected={systemHealth.connections?.database}
                  />
                  {/* Indicador de Comunicación de Datos (Datalink Watchdog) */}
                  {(() => {
                    const datalink = systemHealth.datalink;
                    let visualStatus = 'unknown';
                    let visualLabel = 'INICIALIZANDO';

                    if (datalink) {
                      switch (datalink.status) {
                        case 'ONLINE_GENERANDO':
                          visualStatus = 'online';
                          visualLabel = 'ONLINE';
                          break;
                        case 'ONLINE_SIN_GENERACION':
                          visualStatus = 'warning'; // Ámbar para indicar 0 kW (Corte externo?)
                          visualLabel = 'ONLINE (0 kW)';
                          break;
                        case 'SIN_COMUNICACION':
                        case 'SIN_DATOS':
                          visualStatus = 'offline';
                          visualLabel = 'SIN COMUNICACIÓN';
                          break;
                        default:
                          visualStatus = 'offline';
                          visualLabel = datalink.status;
                      }
                    }

                    return (
                      <CommunicationIndicator
                        status={visualStatus}
                        customLabel={visualLabel}
                        lastDataTime={datalink?.lastDataTimestamp ? new Date(datalink.lastDataTimestamp) : lastDataTime}
                      />
                    );
                  })()}

                  <div className="text-sm text-gray-600">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Uptime: {formatUptime(systemHealth.uptime || 0)}
                  </div>

                  {/* Estado rápido del motor */}
                  {(() => {
                    const isGen = !currentData?.isStale && currentData && (currentData.kwDesp || 0) > 50;
                    return (
                      <div className={`px-2 py-1 rounded border text-[10px] font-bold ${isGen ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                        MOTOR: {isGen ? 'EN MARCHA' : 'PARADO'}
                      </div>
                    );
                  })()}

                  <button
                    onClick={handleLogout}
                    className="ml-4 p-2 text-gray-400 hover:text-red-500 transition-colors"
                    title="Cerrar Sesión"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <TabButton
              active={activeTab === 'dashboard'}
              onClick={() => setActiveTab('dashboard')}
            >
              Dashboard
            </TabButton>
            <TabButton
              active={activeTab === 'alarmas'}
              onClick={() => setActiveTab('alarmas')}
            >
              Alarmas
            </TabButton>
            <TabButton
              active={activeTab === 'ordenes'}
              onClick={() => setActiveTab('ordenes')}
            >
              Órdenes
            </TabButton>
            <TabButton
              active={activeTab === 'novedades'}
              onClick={() => setActiveTab('novedades')}
            >
              Novedades
            </TabButton>
            <TabButton
              active={activeTab === 'informe'}
              onClick={() => setActiveTab('informe')}
            >
              Informe Diario
            </TabButton>
            <TabButton
              active={activeTab === 'cortes'}
              onClick={() => setActiveTab('cortes')}
            >
              Cortes
            </TabButton>
            <TabButton
              active={activeTab === 'auditoria'}
              onClick={() => setActiveTab('auditoria')}
            >
              <span className="flex items-center gap-1">
                <ShieldCheck className="w-4 h-4" />
                Auditoría CAMMESA
              </span>
            </TabButton>
            <TabButton
              active={activeTab === 'config'}
              onClick={() => setActiveTab('config')}
            >
              Configuración
            </TabButton>
            <TabButton
              active={activeTab === 'docs'}
              onClick={() => setActiveTab('docs')}
            >
              Documentación
            </TabButton>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Alerta de Plantas Desconectadas */}
        {showDisconnectedAlert && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
            <div className="flex items-start">
              <AlertTriangle className="w-6 h-6 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-red-800 font-semibold mb-2">⚠️ Plantas Sin Reportar</h3>
                <div className="text-red-700 text-sm space-y-1">
                  {plantasEstado.filter(p => p.estado === 'desconectado').map(planta => (
                    <div key={planta.planta_id} className="flex items-center justify-between">
                      <span>
                        <strong>{planta.planta_id.toUpperCase() === 'GVBIO' ? 'PLANTA 1' : planta.planta_id.toUpperCase()}</strong> -
                        Sin datos desde hace {planta.minutos_sin_reportar} minutos
                      </span>
                      <span className="text-xs text-red-600">
                        Última medición: {new Date(planta.ultima_medicion).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <button
                onClick={() => setShowDisconnectedAlert(false)}
                className="text-red-500 hover:text-red-700 ml-4"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Selector de Planta y Filtros */}
        <div className="mb-6 flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Planta Seleccionada
            </label>
            <select
              value={selectedPlanta}
              onChange={(e) => setSelectedPlanta(e.target.value)}
              className="input w-full"
            >
              {plantas.map(planta => (
                <option key={planta.id} value={planta.id}>
                  {planta.nombre.toUpperCase().includes('GVBIO') ? 'PLANTA 1' : planta.nombre} ({planta.tipo_generacion})
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rango de Tiempo
            </label>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="input w-full"
            >
              <option value="1h">Última Hora</option>
              <option value="6h">Últimas 6 Horas</option>
              <option value="24h">Últimas 24 Horas</option>
              <option value="7d">Últimos 7 Días</option>
              <option value="30d">Últimos 30 Días</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setActiveTab('auditoria')}
              className="group relative flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors h-[42px] items-center space-x-2"
            >
              <ShieldCheck className="w-5 h-5" />
              <span>Auditoría Legal CAMMESA</span>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'dashboard' && (
          <DashboardTab
            currentData={currentData}
            mediciones={mediciones}
            systemHealth={systemHealth}
          />
        )}

        {activeTab === 'alarmas' && (
          <AlarmasPanel plantaId={selectedPlanta} />
        )}

        {activeTab === 'ordenes' && (
          <OrdenesPanel plantaId={selectedPlanta} />
        )}

        {activeTab === 'novedades' && (
          <NovedadesPanel plantaId={selectedPlanta} />
        )}

        {activeTab === 'informe' && (
          <InformeDiario />
        )}

        {activeTab === 'cortes' && (
          <InformeCortes />
        )}

        {activeTab === 'auditoria' && (
          <AuditoriaCammesa />
        )}

        {activeTab === 'config' && (
          <ConfigPanel plantaId={selectedPlanta} />
        )}

        {activeTab === 'docs' && (
          <Documentacion />
        )}
      </main>

      {/* Pop-up crítico de CAMMESA */}
      {cammesaAlert && (
        <CammesaAlertPopup
          mensaje={cammesaAlert}
          onClose={() => {
            // Posponer 5 minutos
            setTimeout(() => setCammesaAlert(cammesaAlert), 5 * 60 * 1000);
            setCammesaAlert(null);
          }}
          onAcknowledge={() => {
            setCammesaAlert(null);
            // Aquí se podría enviar confirmación al backend
          }}
        />
      )}

      {/* Popups de Alarmas Activas */}
      <div className="fixed top-24 right-4 z-[9999] flex flex-col space-y-3 max-w-sm w-full pointer-events-none">
        {alarmas.map(alarma => (
          <AlarmaPopup
            key={alarma.id}
            alarma={alarma}
            onClose={() => setAlarmas(prev => prev.filter(a => a.id !== alarma.id))}
          />
        ))}
      </div>
    </div>
  );
}

// ===== COMPONENTES =====

function StatusBadge({ icon: Icon, label, connected }) {
  return (
    <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm ${connected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}>
      <Icon className="w-4 h-4" />
      <span>{label}</span>
      {connected ? (
        <CheckCircle className="w-4 h-4" />
      ) : (
        <XCircle className="w-4 h-4" />
      )}
    </div>
  );
}

// Indicador de estado de comunicación de datos
function CommunicationIndicator({ status, lastDataTime, customLabel }) {
  const statusConfig = {
    online: {
      bgColor: 'bg-slate-100',
      textColor: 'text-slate-700',
      dotColor: 'bg-emerald-500',
      borderColor: 'border-emerald-400',
      label: 'CONECTADO',
      pulse: true,
      showTime: true
    },
    warning: {
      bgColor: 'bg-amber-50', // Cambiado a Amber 50 para destacar más
      textColor: 'text-amber-800',
      dotColor: 'bg-amber-500',
      borderColor: 'border-amber-400',
      label: 'DEMORA',
      pulse: true,
      showTime: true
    },
    offline: {
      bgColor: 'bg-red-50',
      textColor: 'text-red-700',
      dotColor: 'bg-red-500',
      borderColor: 'border-red-400',
      label: 'SIN COMUNICACIÓN',
      pulse: false,
      showTime: true
    },
    unknown: {
      bgColor: 'bg-slate-100',
      textColor: 'text-slate-500',
      dotColor: 'bg-slate-400',
      borderColor: 'border-slate-300',
      label: 'INICIALIZANDO',
      pulse: true,
      showTime: false
    }
  };

  const config = statusConfig[status] || statusConfig.unknown;

  // Usar label personalizado si existe
  const displayLabel = customLabel || config.label;

  // Contador de tiempo en vivo
  const [timeSince, setTimeSince] = React.useState('');

  React.useEffect(() => {
    const updateTime = () => {
      if (!lastDataTime) {
        setTimeSince('');
        return;
      }
      const seconds = Math.floor((new Date() - lastDataTime) / 1000);
      if (seconds < 60) {
        setTimeSince(`${seconds}s`);
      } else if (seconds < 3600) {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        setTimeSince(`${mins}m ${secs}s`);
      } else {
        const hours = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        setTimeSince(`${hours}h ${mins}m`);
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [lastDataTime]);

  // Si no hay lastDataTime válido en modo offline, mostrar solo '---'
  const timeDisplay = (!config.showTime) ? null : (timeSince || '---');

  return (
    <div className={`flex items-center space-x-2 px-3 py-1.5 rounded text-xs font-semibold ${config.bgColor} ${config.textColor} border ${config.borderColor}`}>
      {/* Indicador LED */}
      <div className="relative flex items-center justify-center">
        <span className={`w-2.5 h-2.5 rounded-full ${config.dotColor}`}></span>
        {config.pulse && (
          <span className={`absolute w-2.5 h-2.5 rounded-full ${config.dotColor} animate-ping opacity-75`}></span>
        )}
      </div>
      <span className="uppercase tracking-wider">{displayLabel}</span>
      {timeDisplay && (
        <span className="font-mono text-xs bg-white px-1.5 py-0.5 rounded border border-slate-200 opacity-80">{timeDisplay}</span>
      )}
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${active
        ? 'border-primary-600 text-primary-600'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
        }`}
    >
      {children}
    </button>
  );
}

function DashboardTab({ currentData, mediciones, systemHealth }) {
  const isStale = currentData?.isStale;
  const datalinkOffline = ['SIN_COMUNICACION', 'SIN_DATOS'].includes(systemHealth?.datalink?.status);
  const isGenerating = !isStale && !datalinkOffline && currentData && (currentData.kwDesp || 0) > 50;
  const isPowerCut = !isStale && !datalinkOffline && currentData &&
    (currentData.tension_vab || currentData.tension_l1 || 0) < 5000;

  const plantStatusLabel = (isStale || datalinkOffline)
    ? 'SIN COMUNICACIÓN'
    : isGenerating
      ? 'GENERANDO EN LÍNEA'
      : isPowerCut
        ? 'CORTE DE ENERGÍA EXTERNA'
        : 'GENERADOR DETENIDO / STANDBY';

  const plantStatusColor = (isStale || datalinkOffline)
    ? 'red'
    : isGenerating
      ? 'emerald'
      : isPowerCut
        ? 'red'
        : 'amber';

  return (
    <div className="space-y-6">
      {/* Estado del Generador - Visibilidad Mejorada */}
      <div className="flex items-center space-x-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="flex items-center space-x-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${plantStatusColor === 'emerald' ? 'bg-emerald-100' : plantStatusColor === 'red' ? 'bg-red-100' : 'bg-amber-100'}`}>
            <Zap className={`w-6 h-6 ${plantStatusColor === 'emerald' ? 'text-emerald-600' : plantStatusColor === 'red' ? 'text-red-600' : 'text-amber-600'} ${isGenerating ? 'animate-pulse' : ''}`} />
          </div>
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest leading-none mb-1">Estado de Planta</p>
            <h2 className={`text-xl font-black ${plantStatusColor === 'emerald' ? 'text-emerald-700' : plantStatusColor === 'red' ? 'text-red-700' : 'text-amber-700'}`}>
              {plantStatusLabel}
            </h2>
            {isStale && (
              <p className="text-xs text-red-500 mt-1">Último dato recibido hace más de 45 segundos</p>
            )}
          </div>
        </div>
        <div className="ml-auto flex items-center space-x-4">
          {isGenerating && (
            <div className="flex flex-col items-end">
              <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">ACTIVO</span>
              <span className="text-lg font-mono font-bold text-gray-800">{currentData.kwDesp?.toFixed(1)} kW</span>
            </div>
          )}
        </div>
      </div>
      {/* Mediciones Actuales - Potencias */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Potencia Activa"
          value={formatMetric(currentData?.kwDesp, 2)}
          unit="kW"
          icon={Zap}
          color="blue"
          stale={currentData?.isStale}
        />
        <MetricCard
          title="Potencia Reactiva"
          value={formatMetric(currentData?.kVAR || currentData?.kVARDesp, 2)}
          unit="kVAR"
          icon={TrendingUp}
          color="indigo"
          stale={currentData?.isStale}
        />
        <MetricCard
          title="Potencia Aparente"
          value={formatMetric(currentData?.kVA, 2)}
          unit="kVA"
          icon={TrendingUp}
          color="purple"
          stale={currentData?.isStale}
        />
        <MetricCard
          title="Factor de Potencia"
          value={formatMetric(currentData?.factor_potencia || currentData?.cosfi, 3)}
          unit="cos φ"
          icon={Activity}
          color="green"
          stale={currentData?.isStale}
        />
      </div>

      {/* Mediciones Actuales - Red eléctrica */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Frecuencia"
          value={formatMetric(currentData?.frecuencia, 2)}
          unit="Hz"
          icon={Activity}
          color="orange"
          stale={currentData?.isStale}
        />
        <MetricCard
          title="Tensión Promedio"
          value={
            (currentData?.tension_promedio || currentData?.tension_vab || currentData?.tension_l1) > 0
              ? formatMetric((currentData?.tension_promedio || currentData?.tension_vab || currentData?.tension_l1) / 1000, 2)
              : '--'
          }
          unit="kV"
          icon={Zap}
          color="cyan"
          stale={currentData?.isStale}
        />
        <MetricCard
          title="Corriente L1"
          value={formatMetric(currentData?.corriente_ia || currentData?.corriente_l1, 1)}
          unit="A"
          icon={Activity}
          color="blue"
          stale={currentData?.isStale}
        />
        <MetricCard
          title="Corriente Promedio"
          value={formatMetric(currentData?.corriente, 1)}
          unit="A"
          icon={Activity}
          color="teal"
          stale={currentData?.isStale}
        />
      </div>

      {/* Gráficos */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">Histórico de Mediciones</h2>
        <MedicionesChart data={mediciones} />
      </div>

      {/* Tensiones y Corrientes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Tensiones</h3>
            <div className="flex space-x-2 text-xs">
              <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-blue-500 mr-1"></span> L1</span>
              <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-emerald-500 mr-1"></span> L2</span>
              <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-amber-500 mr-1"></span> L3</span>
            </div>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <ProgressBar label="L1" value={(currentData?.tension_vab || currentData?.tension_l1 || 0) / 1000} max={36} unit="kV" />
              <ProgressBar label="L2" value={(currentData?.tension_vbc || currentData?.tension_l2 || 0) / 1000} max={36} unit="kV" />
              <ProgressBar label="L3" value={(currentData?.tension_vca || currentData?.tension_l3 || 0) / 1000} max={36} unit="kV" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Corrientes</h3>
            <div className="flex space-x-2 text-xs">
              <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-blue-500 mr-1"></span> L1</span>
              <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-emerald-500 mr-1"></span> L2</span>
              <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-amber-500 mr-1"></span> L3</span>
            </div>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <ProgressBar label="L1" value={currentData?.corriente_ia || currentData?.corriente_l1 || 0} max={250} unit="A" />
              <ProgressBar label="L2" value={currentData?.corriente_ib || currentData?.corriente_l2 || 0} max={250} unit="A" />
              <ProgressBar label="L3" value={currentData?.corriente_ic || currentData?.corriente_l3 || 0} max={250} unit="A" />
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Histórico de Tensiones y Corrientes</h3>
        <TensionesCorrientesChart data={mediciones} />
      </div>

      {/* Estadísticas de Generación - Calculadas desde datos reales */}
      <GenerationStats mediciones={mediciones} currentData={currentData} />
    </div>
  );
}

function formatMetric(value, decimals = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '--';
  return n.toFixed(decimals);
}

function MetricCard({ title, value, unit, icon: Icon, color, stale }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
    green: 'bg-green-50 text-green-600',
    orange: 'bg-orange-50 text-orange-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    cyan: 'bg-cyan-50 text-cyan-600',
    teal: 'bg-teal-50 text-teal-600'
  };

  return (
    <div className={`card ${stale ? 'opacity-75 border-amber-200' : ''}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        <div className={`p-2 rounded-lg ${colorClasses[color] || colorClasses.blue}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <div className="flex items-baseline space-x-2">
        <span className="text-3xl font-bold text-gray-900">{value}</span>
        <span className="text-sm text-gray-500">{unit}</span>
      </div>
      {value === '--' ? (
        <p className="text-[10px] text-gray-400 mt-1 font-medium">Sin dato</p>
      ) : stale ? (
        <p className="text-[10px] text-amber-600 mt-1 font-medium">Dato desactualizado</p>
      ) : (
        <p className="text-[10px] text-emerald-600 mt-1 font-medium animate-pulse">● EN VIVO</p>
      )}
    </div>
  );
}

function ProgressBar({ label, value, max, unit }) {
  const percentage = (value / max) * 100;

  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="font-medium text-gray-700">{label}</span>
        <span className="text-gray-600">{value?.toFixed(2)} {unit}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="bg-primary-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    </div>
  );
}

function StatItem({ label, value }) {
  return (
    <div className="text-center">
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      <div className="text-sm text-gray-600">{label}</div>
    </div>
  );
}

// Componente de Estadísticas de Generación con filtros y exportación
function GenerationStats({ mediciones, currentData }) {
  const [periodoFiltro, setPeriodoFiltro] = React.useState('hoy'); // 'hoy', 'semana', 'mes', 'custom'
  const [fechaDesde, setFechaDesde] = React.useState('');
  const [fechaHasta, setFechaHasta] = React.useState('');

  // Filtrar mediciones según el período seleccionado
  const medicionesFiltradas = React.useMemo(() => {
    if (!mediciones || mediciones.length === 0) return [];

    const now = new Date();
    let inicio, fin;

    switch (periodoFiltro) {
      case 'hoy':
        // El día comienza a las 00:15
        inicio = new Date(now);
        inicio.setHours(0, 15, 0, 0);
        if (now < inicio) {
          // Si aún no son las 00:15, usar el día anterior
          inicio.setDate(inicio.getDate() - 1);
        }
        fin = new Date(inicio);
        fin.setDate(fin.getDate() + 1);
        break;
      case 'semana':
        inicio = new Date(now);
        inicio.setDate(inicio.getDate() - 7);
        inicio.setHours(0, 15, 0, 0);
        fin = now;
        break;
      case 'mes':
        inicio = new Date(now);
        inicio.setMonth(inicio.getMonth() - 1);
        inicio.setHours(0, 15, 0, 0);
        fin = now;
        break;
      case 'custom':
        if (!fechaDesde || !fechaHasta) return mediciones;
        inicio = new Date(fechaDesde + 'T00:15:00');
        fin = new Date(fechaHasta + 'T23:59:59');
        break;
      default:
        return mediciones;
    }

    return mediciones.filter(m => {
      const fecha = new Date(m.timestamp);
      return fecha >= inicio && fecha <= fin;
    });
  }, [mediciones, periodoFiltro, fechaDesde, fechaHasta]);

  // Calcular estadísticas desde los datos filtrados
  const stats = React.useMemo(() => {
    if (!medicionesFiltradas || medicionesFiltradas.length === 0) {
      return {
        totalMediciones: 0,
        promedioKW: '0.00',
        maxKW: '0.00',
        minKW: '0.00',
        tiempoSinGeneracion: 0,
        intervalosParados: 0,
        tiempoConGeneracion: 0,
        tiempoCorteLinea: 0,
        cortesLinea: 0,
        microCortes: 0,
        tiempoSinComunicacion: 0,
        cortesComunicacion: 0,
        energiaTotal: '0.00'
      };
    }

    const UMBRAL_TENSION_MIN = 5000; // Si hay más de 5kV la red está presente
    const UMBRAL_TENSION_CORTE = 5000;
    const UMBRAL_TENSION_MICRO = 20000;
    const UMBRAL_POTENCIA_MIN = 50;
    const UMBRAL_GAP_COMUNICACION = 45000;

    let tiempoSinGeneracionMs = 0;
    let intervalosParados = 0;
    let tiempoConGeneracionMs = 0;
    let tiempoCorteLineaMs = 0;
    let cortesLinea = 0;
    let microCortes = 0;
    let tiempoSinComunicacionMs = 0;
    let cortesComunicacion = 0;
    let paradasGenerador = 0;
    let tiempoParadaGeneradorMs = 0;
    let potencias = [];
    let ultimoCorteLinea = false;
    let ultimoParadaGen = false;
    let inicioCorteLineaTime = null;
    let inicioParadaGenTime = null;
    let energiaTotal = 0;

    // Detectar gap inicial de comunicación si es periodo 'hoy'
    if (periodoFiltro === 'hoy' && medicionesFiltradas.length > 0) {
      const primerDato = new Date(medicionesFiltradas[0].timestamp);
      const nowFn = new Date();
      const inicioDia = new Date(nowFn);
      inicioDia.setHours(0, 15, 0, 0);

      const gapInicial = primerDato - inicioDia;
      if (gapInicial > UMBRAL_GAP_COMUNICACION) {
        tiempoSinComunicacionMs += gapInicial;
        cortesComunicacion++;
      }
    }

    for (let i = 0; i < medicionesFiltradas.length; i++) {
      const m = medicionesFiltradas[i];
      const potencia = m.kwDesp || 0;
      const tension = m.tension_vab || m.tension_l1 || 0;

      potencias.push(potencia);

      let intervaloMs = 10000;
      if (i < medicionesFiltradas.length - 1) {
        const t1 = new Date(m.timestamp);
        const t2 = new Date(medicionesFiltradas[i + 1].timestamp);
        intervaloMs = Math.abs(t2 - t1);

        if (intervaloMs > UMBRAL_GAP_COMUNICACION) {
          tiempoSinComunicacionMs += intervaloMs;
          cortesComunicacion++;
        }

        if (intervaloMs > 600000) intervaloMs = 10000; // Limitar gaps monstruosos en el cálculo de energía
      }

      // Calcular energía siguiendo la lógica de 15 min si es posible, o integración
      energiaTotal += potencia * (intervaloMs / 3600000);

      // 1. CORTE DE LÍNEA (External Power Cut)
      if (tension < UMBRAL_TENSION_CORTE) {
        tiempoCorteLineaMs += intervaloMs;
        if (!ultimoCorteLinea) {
          ultimoCorteLinea = true;
          inicioCorteLineaTime = new Date(m.timestamp).getTime();
        }
      } else {
        if (ultimoCorteLinea) {
          const finCorteTime = new Date(m.timestamp).getTime();
          const duracionCorte = finCorteTime - inicioCorteLineaTime;
          if (duracionCorte >= 5000) {
            cortesLinea++;
          } else if (duracionCorte > 0) {
            microCortes++;
          }
          ultimoCorteLinea = false;
        }

        // 2. PARADA DE GENERADOR (Grid Present, but no power)
        if (tension >= UMBRAL_TENSION_MIN && potencia < UMBRAL_POTENCIA_MIN) {
          tiempoParadaGeneradorMs += intervaloMs;
          intervalosParados++; // Añadir cuenta de intervalos parados directamente
          if (!ultimoParadaGen) {
            ultimoParadaGen = true;
            inicioParadaGenTime = new Date(m.timestamp).getTime();
          }
        } else {
          if (ultimoParadaGen) {
            const finParadaTime = new Date(m.timestamp).getTime();
            const duracionParada = finParadaTime - inicioParadaGenTime;
            if (duracionParada > 10000) {
              paradasGenerador++;
            }
            ultimoParadaGen = false;
          }
        }

        if (potencia >= UMBRAL_POTENCIA_MIN) {
          tiempoConGeneracionMs += intervaloMs;
        }
      }
    }

    const potenciasValidas = potencias.filter(p => p > 0);
    const promedioKW = potenciasValidas.length > 0
      ? potenciasValidas.reduce((a, b) => a + b, 0) / potenciasValidas.length
      : 0;
    const maxKW = potencias.length > 0 ? Math.max(...potencias) : 0;
    const minKW = potenciasValidas.length > 0 ? Math.min(...potenciasValidas) : 0;

    return {
      totalMediciones: medicionesFiltradas.length,
      promedioKW: promedioKW.toFixed(2),
      maxKW: maxKW.toFixed(2),
      minKW: minKW.toFixed(2),
      tiempoSinGeneracion: Math.floor(tiempoParadaGeneradorMs / 1000),
      paradasGenerador,
      tiempoConGeneracion: Math.floor(tiempoConGeneracionMs / 1000),
      tiempoCorteLinea: Math.floor(tiempoCorteLineaMs / 1000),
      cortesLinea,
      microCortes,
      tiempoSinComunicacion: Math.floor(tiempoSinComunicacionMs / 1000),
      cortesComunicacion,
      energiaTotal: energiaTotal.toFixed(2)
    };
  }, [medicionesFiltradas]);

  const formatTiempo = (segundos) => {
    if (segundos < 60) return `${segundos}s`;
    if (segundos < 3600) return `${Math.floor(segundos / 60)}m ${segundos % 60}s`;
    const horas = Math.floor(segundos / 3600);
    const minutos = Math.floor((segundos % 3600) / 60);
    return `${horas}h ${minutos}m`;
  };

  const generadorParado = currentData &&
    (currentData.tension_vab || currentData.tension_l1 || 0) > 30000 &&
    (currentData.kwDesp || 0) < 50;

  // Exportar a Excel
  const exportarExcel = () => {
    const periodoTexto = {
      'hoy': 'Día Actual (desde 00:15)',
      'semana': 'Última Semana',
      'mes': 'Último Mes',
      'custom': `${fechaDesde} a ${fechaHasta}`
    };

    let csv = 'ESTADÍSTICAS DE GENERACIÓN\n';
    csv += `Período,${periodoTexto[periodoFiltro]}\n`;
    csv += `Fecha exportación,${new Date().toLocaleString()}\n\n`;

    csv += 'RESUMEN\n';
    csv += `Mediciones,${stats.totalMediciones}\n`;
    csv += `Energía Total (kWh),${stats.energiaTotal}\n`;
    csv += `Potencia Promedio (kW),${stats.promedioKW}\n`;
    csv += `Potencia Máxima (kW),${stats.maxKW}\n`;
    csv += `Potencia Mínima (kW),${stats.minKW}\n`;
    csv += `Tiempo Generando,${formatTiempo(stats.tiempoConGeneracion)}\n`;
    csv += `Tiempo Gen. Parado,${formatTiempo(stats.tiempoSinGeneracion)}\n`;
    csv += `Intervalos Parado,${stats.intervalosParados}\n`;
    csv += `Tiempo Corte Línea,${formatTiempo(stats.tiempoCorteLinea)}\n`;
    csv += `Cantidad Cortes Línea,${stats.cortesLinea}\n`;
    csv += `Cantidad Microcortes,${stats.microCortes}\n`;
    csv += `Tiempo Sin Comunicación,${formatTiempo(stats.tiempoSinComunicacion)}\n`;
    csv += `Cortes de Comunicación,${stats.cortesComunicacion}\n`;

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `estadisticas_generacion_${periodoFiltro}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <div className="card border border-gray-200">
      {/* Header con filtros */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-4 pb-3 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800">Estadísticas de Generación</h3>

        <div className="flex items-center space-x-3">
          {/* Selector de período */}
          <select
            value={periodoFiltro}
            onChange={(e) => setPeriodoFiltro(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="hoy">Hoy (desde 00:15)</option>
            <option value="semana">Última Semana</option>
            <option value="mes">Último Mes</option>
            <option value="custom">Personalizado</option>
          </select>

          {periodoFiltro === 'custom' && (
            <>
              <input
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                className="px-2 py-1 border border-gray-300 rounded text-sm"
              />
              <span className="text-gray-500">a</span>
              <input
                type="date"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                className="px-2 py-1 border border-gray-300 rounded text-sm"
              />
            </>
          )}

          {/* Botón exportar */}
          <button
            onClick={exportarExcel}
            className="px-3 py-1.5 bg-slate-700 text-white text-sm rounded hover:bg-slate-800 flex items-center space-x-1"
          >
            <Database className="w-4 h-4" />
            <span>Exportar</span>
          </button>
        </div>
      </div>

      {/* Indicador de período */}
      <div className="mb-3 text-xs text-gray-500 uppercase tracking-wide">
        {periodoFiltro === 'hoy' && 'Reset diario a las 00:15 hs'}
        {periodoFiltro === 'semana' && 'Últimos 7 días'}
        {periodoFiltro === 'mes' && 'Últimos 30 días'}
        {periodoFiltro === 'custom' && fechaDesde && fechaHasta && `Del ${fechaDesde} al ${fechaHasta}`}
      </div>

      {/* Alerta de generador parado */}
      {generadorParado && (
        <div className="mb-4 p-3 bg-amber-50 border-l-4 border-amber-500 flex items-center space-x-3">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <span className="text-amber-800 font-medium">
            GENERADOR PARADO - Línea externa OK pero sin generación
          </span>
        </div>
      )}

      {/* Grid de estadísticas - Diseño profesional */}
      <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-2">
        <div className="text-center p-3 bg-slate-50 border border-slate-200 rounded">
          <div className="text-xl font-bold text-slate-800">{stats.totalMediciones}</div>
          <div className="text-xs text-slate-500 uppercase">Mediciones</div>
        </div>

        <div className="text-center p-3 bg-slate-50 border border-slate-200 rounded">
          <div className="text-xl font-bold text-slate-800">{stats.energiaTotal}</div>
          <div className="text-xs text-slate-500 uppercase">kWh Total</div>
        </div>

        <div className="text-center p-3 bg-slate-50 border border-slate-200 rounded">
          <div className="text-xl font-bold text-slate-800">{stats.promedioKW}</div>
          <div className="text-xs text-slate-500 uppercase">Promedio kW</div>
        </div>

        <div className="text-center p-3 bg-slate-50 border border-slate-200 rounded">
          <div className="text-xl font-bold text-slate-800">{stats.maxKW}</div>
          <div className="text-xs text-slate-500 uppercase">Máximo kW</div>
        </div>

        <div className="text-center p-3 bg-slate-50 border border-slate-200 rounded">
          <div className="text-xl font-bold text-slate-800">{stats.minKW}</div>
          <div className="text-xs text-slate-500 uppercase">Mínimo kW</div>
        </div>

        <div className={`text-center p-3 rounded border ${stats.tiempoSinGeneracion > 0 ? 'bg-amber-50 border-amber-300' : 'bg-slate-50 border-slate-200'}`}>
          <div className={`text-xl font-bold ${stats.tiempoSinGeneracion > 0 ? 'text-amber-700' : 'text-slate-800'}`}>
            {formatTiempo(stats.tiempoSinGeneracion)}
          </div>
          <div className={`text-xs uppercase ${stats.tiempoSinGeneracion > 0 ? 'text-amber-600' : 'text-slate-500'}`}>
            Gen. Parado
          </div>
        </div>

        <div className={`text-center p-3 rounded border ${stats.tiempoCorteLinea > 0 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
          <div className={`text-xl font-bold ${stats.tiempoCorteLinea > 0 ? 'text-red-700' : 'text-slate-800'}`}>
            {formatTiempo(stats.tiempoCorteLinea)}
          </div>
          <div className="text-xs uppercase text-slate-500">Duración Fallas</div>
        </div>

        <div className="text-center p-3 bg-slate-50 border border-slate-200 rounded">
          <div className="text-xl font-bold text-amber-600">{stats.paradasGenerador}</div>
          <div className="text-xs text-slate-500 uppercase">Paradas Gen.</div>
        </div>

        <div className="text-center p-3 bg-slate-50 border border-slate-200 rounded">
          <div className="text-xl font-bold text-red-600">{stats.cortesLinea}</div>
          <div className="text-xs text-slate-500 uppercase">Cortes Red</div>
        </div>

        <div className="text-center p-3 bg-slate-50 border border-slate-200 rounded">
          <div className="text-xl font-bold text-orange-600">{stats.microCortes}</div>
          <div className="text-xs text-slate-500 uppercase">Microcortes</div>
        </div>

        <div className="text-center p-3 bg-slate-50 border border-slate-200 rounded">
          <div className="text-xl font-bold text-slate-800">{stats.cortesComunicacion}</div>
          <div className="text-xs text-slate-500 uppercase">Gaps Datos</div>
        </div>
      </div>

      {/* Alertas detalladas - Sin emojis */}
      {(stats.intervalosParados > 0 || stats.cortesLinea > 0 || stats.cortesComunicacion > 0) && (
        <div className="mt-4 pt-3 border-t border-gray-200 space-y-1 text-sm">
          {stats.intervalosParados > 0 && (
            <div className="flex items-center space-x-2 text-amber-700">
              <AlertTriangle className="w-4 h-4" />
              <span>{stats.intervalosParados} intervalos con generador parado (tensión OK, potencia {'<'} 50kW)</span>
            </div>
          )}
          {stats.cortesLinea > 0 && (
            <div className="flex items-center space-x-2 text-red-700">
              <Zap className="w-4 h-4" />
              <span>{stats.cortesLinea} Cortes de Energía (tensión {'<'} 5kV por {'>'} 5s)</span>
            </div>
          )}
          {stats.microCortes > 0 && (
            <div className="flex items-center space-x-2 text-orange-700">
              <Zap className="w-4 h-4" />
              <span>{stats.microCortes} Microcortes de Tensión (bajones breves o cortes {'<'} 5s)</span>
            </div>
          )}
          {stats.cortesComunicacion > 0 && (
            <div className="flex items-center space-x-2 text-red-800 font-semibold">
              <WifiOff className="w-4 h-4" />
              <span>{stats.cortesComunicacion} Cortes de Datos detectados (gaps {'>'} 45s) - CRÍTICO PARA CAMMESA</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default App;

/**
 * Componente AlarmaPopup
 * Muestra una alerta emergente que requiere reconocimiento
 */
function AlarmaPopup({ alarma, onClose }) {
  const [recognizing, setRecognizing] = useState(false);
  const isCritical = alarma.tipo === 'CRÍTICO';
  const colorClass = isCritical ? 'red' : alarma.tipo === 'ALERTA' ? 'amber' : 'blue';

  const handleRecognize = () => {
    setRecognizing(true);
    // Simular guardado y cerrar
    setTimeout(onClose, 600);
  };

  return (
    <div className={`pointer-events-auto bg-white border-l-4 border-${colorClass}-600 shadow-2xl rounded-lg overflow-hidden animate-in slide-in-from-right duration-300`}>
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0 pt-0.5">
            <AlertTriangle className={`h-6 w-6 text-${colorClass}-600 ${isCritical ? 'animate-pulse' : ''}`} />
          </div>
          <div className="ml-3 w-0 flex-1">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">
              {alarma.tipo} • {new Date(alarma.timestamp).toLocaleTimeString()}
            </p>
            <p className="text-sm font-bold text-gray-900 leading-tight">
              {alarma.mensaje}
            </p>
            <div className="mt-4 flex">
              <button
                onClick={handleRecognize}
                disabled={recognizing}
                className={`flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-xs font-bold rounded shadow-sm text-white bg-${colorClass}-600 hover:bg-${colorClass}-700 focus:outline-none transition-all ${recognizing ? 'opacity-50' : ''}`}
              >
                {recognizing ? 'RECONOCIENDO...' : 'RECONOCER'}
              </button>
            </div>
          </div>
          <div className="ml-4 flex-shrink-0 flex">
            <button
              onClick={onClose}
              className="bg-white rounded-md inline-flex text-gray-300 hover:text-gray-400 focus:outline-none"
            >
              <XCircle className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

